/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { ReactNode } from "react";

import { truncate } from 'lodash';

import { useNavigate, } from "react-router-dom";

// language
import { injectIntl, IntlShape } from 'react-intl';

// @mui material components
import { Theme } from "@mui/material/styles";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Declaring prop types for DataTableBodyCell
interface Props {
  intl: IntlShape;
  children: ReactNode;
  noBorder?: boolean;
  align?: "left" | "right" | "center";
  cell?: any;
  title?: string;
  isNewRow?: boolean;
}

function DataTableBodyCell({intl, noBorder, align, children, cell, title, isNewRow }: Props): JSX.Element {

  const navigate = useNavigate();

  const shortenText = ( str: string, len = 10 ): string => {
    return truncate(str, { length: len })
  }

  const renderContent = (content: React.ReactNode): React.ReactNode => {
    if (typeof content === 'string') {
      // Truncate text content
      return shortenText(content);
    } else if (React.isValidElement(content)) {
      // Leave other elements unchanged
      return content;
    } else {
      // Handle other content types as needed
      return String(content); // Convert to string, you can customize this behavior
    }
  };

   // go to profile page of user
   const handleDetailItem = ( id: number ) => {
    navigate( (`/admin/users/user-profile/${id}`), { state: {
      id: id,
      }}
    );
  }

  // console.log(cell.row.original.id)

  return (
    <MDBox
      component="td"
      textAlign={align}
      py={1.5}
      // px={0.5}
      sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }: Theme) => ({
        fontSize: size.sm,
        borderBottom: noBorder ? "none" : `${borderWidth[1]} solid ${light.main}`,
        paddingLeft: "1.1rem", paddingBottom:'0.3rem', paddingTop:'0.3rem',
      })}
      className={cell.column.id}
    >
      <MDBox
        display="inline-block"
        width="max-content"
        color="text"
        sx={{ 
          verticalAlign: "middle",
          color: cell.column.id === 'returnPlace'
            ? '#309B0A'
            : (title === intl.formatMessage({ id: 'tableContainers.title' }) &&
              cell.column.id === 'isEnabled')
              ? (cell.row.original.isEnabled === 'V prevádzke'
                ? '#309B0A'
                : cell.row.original.isEnabled === 'Mimo prevádzky'
                  ? '#DD2626'
                  : 'text.secondary')
              : undefined,
          fontWeight: 
            ((cell.column.id === 'name' || cell.column.id === 'equipmentCount' 
            ||  cell.column.id === 'fullName' 
            ||  (cell.column.id === 'type' && title !== intl.formatMessage( {id: 'tablePayments.title'}) ))) ||
            (isNewRow && (cell.column.id === 'id')) 
            ? 700 : undefined,
          cursor:
            (cell.column.id === 'fullName' || cell.column.id === 'id') &&
            title === intl.formatMessage({ id: 'tableUsers.title' })
              ? 'pointer'
              : 'default',
        }}
        onClick={() => {
          if (
            (cell.column.id === 'fullName' || cell.column.id === 'id') &&
            title === intl.formatMessage({ id: 'tableUsers.title' })
          ) {
            handleDetailItem(cell.row.original.id);
          }
        }}
      >
        {renderContent(children)}
        
        {/* users, orders */}
        {cell.column.id === 'fullName' && cell.row.original.email && (
          <MDBox mt={0.5} fontSize="small" 
            color="text.secondary"
            fontWeight= {isNewRow && cell.row.original.email ? 700 : 400}
          >
            {cell.row.original.email}
          </MDBox>
        )}
       
        {/* fines in order */}
        {title === intl.formatMessage({ id: 'fines.detail.title' }) && cell.column.id === 'type' && cell.row.original.id && (
          <MDBox mt={0.5} fontSize="small" fontWeight="400" color="text.secondary">
            {cell.row.original.id}
          </MDBox>
        )}
       
        {/* items in order */}
        {cell.column.id === 'rentPlace' && cell.row.original.rentedAt && (
          <MDBox mt={0.5} fontSize="0.625rem" fontWeight="400" color="text.secondary">
            {cell.row.original.rentedAt}
          </MDBox>
        )}
        
        {cell.column.id === 'returnPlace' && cell.row.original.returnedAt && (
          <MDBox mt={0.5} fontSize="0.625rem" fontWeight="400" color="#7b809a">
            {cell.row.original.returnedAt}
          </MDBox>
        )}
      
        {/* containers */}
        { title === intl.formatMessage( {id: 'tableContainers.title'} ) && (
          <>
            {cell.column.id === 'name' && cell.row.original.id && (
              <MDBox mt={0.5} fontSize="small" color="text.secondary"
                sx={{fontWeight: 400}}
              >
                {cell.row.original.id}
              </MDBox>
            )}
            
            { (cell.column.id === 'isEnabled' && cell.row.original.status) && (
              <MDBox
                mt={0.5}
                fontSize="small"
                style={{
                  color: cell.row.original.status === 'Online'
                    ? '#309B0A'
                    : cell.row.original.status === 'Offline'
                    ? '#F5A524'
                    : undefined,
                }}
              >
                {cell.row.original.status}
              </MDBox>
            )}
          </>
        )}
      </MDBox>
    </MDBox>
  );
}

// Declaring default props for DataTableBodyCell
DataTableBodyCell.defaultProps = {
  noBorder: false,
  align: "left" as "left",
};

export default injectIntl(DataTableBodyCell);
